/* ============================================================
   VEYLA | homepage behaviour

   A homepage, not a product page. It carries the shop module (three sizes,
   subscribe switch, one button) and the proof, and it deliberately does NOT
   carry the product-page furniture: no thinning-zone picker, no accordions,
   no tabbed review wall, no proof popup. Those live on the PDP, and the links
   in the shop module and the FAQ are how she gets to them.

   The prices, the Seal plan and the add-to-cart payload are NOT in this file.
   They live in shared/vy-offer.js, which the PDP reads too, so the two
   surfaces cannot drift. Edit a number there, never here.

   Every add-to-cart goes through VYDrawer.add(). Nothing here clicks another
   widget's button or borrows another app's open-cart function.
   ============================================================ */
(() => {
  const $  = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];

  const O = window.VY_OFFER;
  const { TIERS, money, planFor, subCents } = O;

  /* zone is null on purpose. The PDP asks her where she is thinning and sends
     the answer as a line property; this page never asks, so it must not stamp
     a default onto her order. shared/vy-offer.js omits the property when the
     zone is null. */
  const state = { zone: null, tier: O.TIER_DEFAULT, sub: O.SUB_DEFAULT };

  const calmMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- header menu (mobile) ---------- */
  const burger = $('#vy-burger');
  const nav    = $('#vy-nav');
  if (burger && nav) {
    burger.addEventListener('click', () => {
      const open = nav.classList.toggle('is-open');
      burger.setAttribute('aria-expanded', String(open));
    });
  }

  /* ---------- hero jump links ----------
     Anchors rather than buttons, so they work with no JS and can be opened in
     a new tab. This only softens the jump and closes the mobile menu behind
     it. The scroll offset is CSS (scroll-padding-top), not a magic number. */
  $$('.vyh-jump').forEach(a => a.addEventListener('click', e => {
    const target = $(a.getAttribute('href'));
    if (!target) return;                       // let the browser deal with it
    e.preventDefault();
    nav && nav.classList.remove('is-open');
    target.scrollIntoView({ behavior: calmMotion ? 'auto' : 'smooth', block: 'start' });
    // move focus too, or a keyboard user is left at the top of the page
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }));

  /* ---------- subscribe switch ---------- */
  const subBox = $('#vy-sub');
  const subSwitch = $('#vy-sub-switch');
  if (subSwitch) subSwitch.addEventListener('click', () => { state.sub = !state.sub; render(); });

  /* ---------- size cards ---------- */
  function pickTier(key) {
    state.tier = key;
    $$('.vy-tier').forEach(el => el.setAttribute('aria-checked', String(el.dataset.tier === key)));
    render();
  }
  $$('.vy-tier').forEach(el => {
    el.addEventListener('click', () => pickTier(el.dataset.tier));
    el.addEventListener('keydown', e => {
      if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); pickTier(el.dataset.tier); }
    });
  });

  /* ---------- render price into the ladder, the CTAs and both bars ---------- */
  function render() {
    const t = TIERS[state.tier];
    const total = O.tierTotal(state.tier, state.sub);

    if (subBox) {
      subBox.dataset.on = String(state.sub);
      subSwitch.setAttribute('aria-checked', String(state.sub));
      const save = $('#vy-sub-save');
      if (save) save.textContent = `Save ${money(t.totalCents - subCents(t.totalCents))}`;
      const terms = $('#vy-sub-terms');
      if (terms) {
        terms.hidden = !state.sub;
        const plan = planFor(state.tier);
        terms.textContent = `Renews ${plan.label} at ${money(total)}. Skip or cancel any time before the next charge.`;
      }
    }

    $$('.vy-tier').forEach(el => {
      const key = el.dataset.tier;
      const cfg = TIERS[key];
      if (!cfg) return;
      const em = el.querySelector('.vy-price em');
      const s  = el.querySelector('.vy-price s');
      const sv = el.querySelector('.vy-save');
      const now = O.tierTotal(key, state.sub);
      if (em) em.textContent = money(now);
      // subscribed: strike the one-time price. one-time: strike the RRP.
      if (s) s.textContent = money(state.sub ? cfg.totalCents : cfg.compareCents);
      if (sv) sv.textContent = `You save ${money(cfg.compareCents - now)}`;
    });

    /* Their ladder prices six tubes at a worse per-tube than four. The note is
       recomputed rather than trusted, so it disappears by itself if they ever
       reprice, and it never shows on a tier where it is not true. */
    const honest = $('#vy-tier-honest');
    if (honest) honest.hidden = !(state.tier === 'b3g3' && O.tier3IsWorseValue(state.sub));

    const label = `Add to cart • ${money(total)}`;
    // the top banner carries its own price column, so its button stays short
    $$('.vy-atc').forEach(b => { b.textContent = b.hasAttribute('data-compact') ? 'Add to cart' : label; });

    const per = $('#vy-per-tube');
    if (per) per.textContent = `${money(O.perTube(state.tier, state.sub))} per tube`;

    // no zone on this page, so the config line is the size and the plan
    const config = `${t.tubes} tubes${state.sub ? ' • Subscription' : ''}`;

    const sk = $('#vy-sticky-sub');
    if (sk) sk.textContent = config;

    const tbNow = $('#vy-tb-now');
    if (tbNow) {
      tbNow.textContent = money(total);
      $('#vy-tb-was').textContent = money(t.compareCents);
      $('#vy-tb-sub').textContent = config;
    }
  }

  /* ---------- add to cart ----------
     One writer. The shop module hands the payload to VYDrawer.add(), which
     posts to /cart/add.js on a real storefront and keeps it in memory here. */
  async function addToCart() {
    const items = O.addPayload(state);
    const btns = $$('.vy-atc');
    const labels = btns.map(b => b.textContent);
    btns.forEach(b => { b.disabled = true; });
    try {
      if (window.VYDrawer) await window.VYDrawer.add(items);
    } catch (err) {
      console.error('[vy-home]', err);
      btns.forEach(b => { b.textContent = 'Something went wrong, try again'; });
      setTimeout(() => btns.forEach((b, i) => { b.textContent = labels[i]; }), 2200);
    } finally {
      btns.forEach(b => { b.disabled = false; });
    }
  }
  $$('.vy-atc').forEach(b => b.addEventListener('click', addToCart));

  /* ---------- before / after sliders ----------
     Six on the proof rail. Reveal is a clip-path rather than a width change,
     which keeps the photos pixel aligned. A mouse can grab anywhere; a finger
     has to start on the handle or the gesture belongs to the scroller. */
  $$('.vy-ba-stage[data-ba]').forEach((stageEl, idx) => {
    const clip   = $('.vy-ba-after', stageEl);
    const handle = $('.vy-ba-handle', stageEl);
    const range  = $('.vy-ba-range', stageEl);
    let dragging = false, touched = false;

    function setPos(pct) {
      const p = Math.min(100, Math.max(0, pct));
      clip.style.clipPath = `inset(0 ${100 - p}% 0 0)`;
      handle.style.left = p + '%';
      range.value = p;
    }
    const posFromEvent = e => {
      const r = stageEl.getBoundingClientRect();
      return ((e.clientX - r.left) / r.width) * 100;
    };

    const grab = e => {
      if (e.pointerType !== 'mouse' && !e.target.closest('.vy-ba-handle')) return;
      dragging = true; touched = true;
      if (e.pointerType !== 'mouse') e.preventDefault();
      stageEl.setPointerCapture(e.pointerId);
      setPos(posFromEvent(e));
    };
    stageEl.addEventListener('pointerdown', grab);
    stageEl.addEventListener('pointermove', e => { if (dragging) setPos(posFromEvent(e)); });
    window.addEventListener('pointermove', e => { if (dragging) setPos(posFromEvent(e)); });
    window.addEventListener('pointerup', () => { dragging = false; });
    window.addEventListener('pointercancel', () => { dragging = false; });
    range.addEventListener('input', e => { touched = true; setPos(Number(e.target.value)); });

    setPos(50);

    /* One sweep the first time a pair scrolls into view: it teaches the drag
       without a tooltip, runs once, and stops the moment she touches it. */
    if (calmMotion || !('IntersectionObserver' in window)) return;
    const hint = () => {
      const from = 50, to = idx % 2 ? 30 : 70, t0 = performance.now(), dur = 1500;
      stageEl.classList.add('is-hinting');
      const step = now => {
        if (touched) { stageEl.classList.remove('is-hinting'); return; }
        const k = Math.min(1, (now - t0) / dur);
        const ease = 0.5 - Math.cos(k * Math.PI * 2) / 2;      // out and back
        setPos(from + (to - from) * ease);
        if (k < 1) requestAnimationFrame(step);
        else { setPos(50); stageEl.classList.remove('is-hinting'); }
      };
      requestAnimationFrame(step);
    };
    new IntersectionObserver((entries, obs) => {
      entries.forEach(en => {
        if (!en.isIntersecting) return;
        obs.disconnect();
        setTimeout(hint, 180 * idx);   // stagger, so they never sweep in unison
      });
    }, { threshold: 0.45 }).observe(stageEl);
  });

  /* ---------- before / after rail ---------- */
  const baRail  = $('#vy-ba-rail');
  const baTrack = $('#vy-ba-track');
  if (baRail && baTrack) {
    const step = () => {
      const card = $('.vy-ba-card', baTrack);
      if (!card) return baTrack.clientWidth;
      const gap = parseFloat(getComputedStyle(baTrack).columnGap) || 0;
      // page by whole cards, but never further than one screen of the rail
      const per = Math.max(1, Math.floor(baTrack.clientWidth / (card.offsetWidth + gap)));
      return (card.offsetWidth + gap) * per;
    };
    const syncRail = () => {
      const max = baTrack.scrollWidth - baTrack.clientWidth;
      const x = baTrack.scrollLeft;
      const atStart = x <= 2, atEnd = x >= max - 2;
      baRail.dataset.atStart = String(atStart);
      baRail.dataset.atEnd = String(atEnd || max <= 0);
      $$('[data-ba-nav]', baRail).forEach(b => {
        b.disabled = Number(b.dataset.baNav) < 0 ? atStart : (atEnd || max <= 0);
      });
    };
    $$('[data-ba-nav]', baRail).forEach(btn => btn.addEventListener('click', () => {
      baTrack.scrollBy({ left: Number(btn.dataset.baNav) * step(), behavior: calmMotion ? 'auto' : 'smooth' });
    }));
    baTrack.addEventListener('scroll', syncRail, { passive: true });
    window.addEventListener('resize', syncRail);
    syncRail();
    // images settle after layout and change scrollWidth, so check again once loaded
    window.addEventListener('load', syncRail);
  }

  /* ---------- groups that fly in once ---------- */
  ['.vyh-range-grid', '.vyh-story-points'].forEach(sel => {
    const group = $(sel);
    if (!group) return;
    if (calmMotion || !('IntersectionObserver' in window)) { group.classList.add('is-in'); return; }
    new IntersectionObserver((entries, obs) => {
      entries.forEach(en => {
        if (!en.isIntersecting) return;
        group.classList.add('is-in');
        obs.disconnect();
      });
    }, { threshold: 0.25 }).observe(group);
  });

  /* ---------- survey stat bar ---------- */
  const statbar = $('#vy-statbar');
  if (statbar) {
    const lines = $$('.vy-statbar-lines li', statbar);
    const arc   = $('#vy-statbar-arc');
    const pct   = $('#vy-statbar-pct');
    const dots  = $('#vy-statbar-dots');
    const CIRC  = 91.1;                       // 2 * pi * r, r = 14.5
    let at = 0, timer = null;

    lines.forEach((_, i) => {
      const b = document.createElement('b');
      if (i === 0) b.className = 'is-on';
      dots.appendChild(b);
    });
    const marks = $$('b', dots);

    function show(i) {
      at = (i + lines.length) % lines.length;
      const n = Number(lines[at].dataset.pct);
      lines.forEach((li, k) => li.classList.toggle('is-on', k === at));
      marks.forEach((b, k) => b.classList.toggle('is-on', k === at));
      pct.textContent = n + '%';
      arc.style.strokeDashoffset = String(CIRC * (1 - n / 100));
    }
    const stop = () => { clearInterval(timer); timer = null; };
    const play = () => { if (!timer && !calmMotion) timer = setInterval(() => show(at + 1), 4200); };

    show(0);
    statbar.addEventListener('pointerenter', stop);
    statbar.addEventListener('pointerleave', play);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => (e.isIntersecting ? play() : stop()), { threshold: 0 }).observe(statbar);
    } else {
      play();
    }
  }

  /* ---------- footer signup (prototype: no backend) ---------- */
  const footForm = $('#vy-foot-form');
  if (footForm) {
    footForm.addEventListener('submit', e => {
      e.preventDefault();
      const email = $('#vy-foot-email');
      if (!email.value || !email.checkValidity()) return;
      footForm.classList.add('is-done');
      email.value = '';
      email.placeholder = 'Thank you, you are on the list';
      email.disabled = true;
      const note = $('#vy-foot-formnote');
      if (note) note.textContent = 'Check your inbox for the week-one photo guide. One email a month from here.';
    });
  }

  /* ---------- sticky ATC + header banner ----------
     Both bars come on once the shop module has scrolled past, and both leave
     the moment the footer arrives: she is reading contact details and policy
     links down there, and two bars over that is nagging rather than selling.

     THE HOMEPAGE DIFFERENCE: the shop module sits in the middle of the page,
     not at the top, so the bars must also stay OFF above it. The hero has its
     own call to action, and a sticky bar covering the hero on load is the
     single most common way this pattern is got wrong. `pastBuybox` is only
     true once the block has left upwards. */
  const sticky = $('#vy-sticky');
  const topbar = $('#vy-topbar');
  const buybox = $('#vy-buybox');
  const foot   = $('.vy-foot');

  let pastBuybox = false, atFooter = false;
  const syncBars = () => {
    const on = pastBuybox && !atFooter;
    if (sticky) sticky.classList.toggle('is-on', on);
    if (topbar) {
      topbar.classList.toggle('is-on', on);
      topbar.setAttribute('aria-hidden', String(!on));
    }
  };

  if (buybox && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => {
      pastBuybox = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      syncBars();
    }, { threshold: 0 }).observe(buybox);

    if (foot) {
      new IntersectionObserver(([entry]) => {
        atFooter = entry.isIntersecting;
        syncBars();
      }, { threshold: 0 }).observe(foot);
    }
  }

  /* ---------- init ---------- */
  pickTier(state.tier);
})();
